import Component from '@glimmer/component'
import { service } from '@ember/service'
import { and, eq } from 'ember-truth-helpers'
import { t } from 'ember-intl'
import { SubscriptionType } from 'teacher-ui-client/subscription-type/subscription-types'
import type SessionService from 'teacher-ui-client/services/session'
import PageHeader from 'teacher-ui-client/components/page-header'
import RolloverHeaderReturnLink from 'teacher-ui-client/components/rollover/header-return-link'
import { SubscriptionFormSummary } from 'teacher-ui-client/components/subscription-form/summary'
import { SubscriptionFormReadingAu } from 'teacher-ui-client/components/subscription-form/reading-au'
import { SubscriptionFormReadingNonAu } from 'teacher-ui-client/components/subscription-form/reading-non-au'
import { SubscriptionFormMaths } from 'teacher-ui-client/components/subscription-form/maths'
import { SubscriptionFormWriting } from 'teacher-ui-client/components/subscription-form/writing'
import type ManageSubscriptionRoute from 'teacher-ui-client/manage/subscription/route'
import { OneRosterNotice } from 'teacher-ui-client/components/manage/one-roster/notice'
import config from 'teacher-ui-client/config/environment'
import { join } from 'teacher-ui-client/utils/uri'
import { canAccess } from 'teacher-ui-client/helpers/can-access'

const oneRosterUrl = join(config.oneRosterUrl, 'login').toString().concat('?return_to=/manage-subscriptions')

interface Signature {
  Args: {
    model: ModelFor<ManageSubscriptionRoute>
  }
}

export class ManageSubscriptionPage extends Component<Signature> {
  @service session!: SessionService

  get school() {
    return this.session.currentSchool
  }

  get isReading() {
    return this.args.model.subscriptionType === SubscriptionType.Reading
  }

  get isMaths() {
    return this.args.model.subscriptionType === SubscriptionType.Maths
  }

  get isWriting() {
    return this.args.model.subscriptionType === SubscriptionType.Writing
  }

  <template>
    <PageHeader>
      <:title>
        <h1>
          {{t "manage.title.subscription"}}
        </h1>
      </:title>
      <:left>
        <RolloverHeaderReturnLink />
      </:left>
    </PageHeader>
    {{#if (canAccess "one_roster")}}
      <OneRosterNotice @linkUrl={{oneRosterUrl}} @linkText={{t "manage.3essentials.marketingTile.goToSubscriptions"}} />
    {{else}}
      <SubscriptionFormSummary
        class="mt-4 mb-8"
        @schoolName={{this.school.name}}
        @schoolSubscription={{@model.lastSchoolSubscription}}
        @trialTeacherCount={{@model.trialTeacherCount}}
        @trialStudentCount={{@model.trialStudentCount}}
      />
      {{#if (and this.isReading (eq this.school.countryCode "AU"))}}
        <SubscriptionFormReadingAu
          @softCapacity={{@model.lastSchoolSubscription.softCapacity}}
          @partnerName={{@model.partnerName}}
          @partnerPhone={{@model.partnerPhone}}
          @partnerEmail={{@model.partnerEmail}}
        />
      {{else if this.isReading}}
        <SubscriptionFormReadingNonAu
          @partnerName={{@model.partnerName}}
          @partnerPhone={{@model.partnerPhone}}
          @partnerEmail={{@model.partnerEmail}}
        />
      {{else if this.isMaths}}
        <SubscriptionFormMaths
          @softCapacity={{@model.lastSchoolSubscription.softCapacity}}
          @partnerName={{@model.partnerName}}
          @partnerPhone={{@model.partnerPhone}}
          @partnerEmail={{@model.partnerEmail}}
        />
      {{else if this.isWriting}}
        <SubscriptionFormWriting
          @softCapacity={{@model.lastSchoolSubscription.softCapacity}}
          @partnerName={{@model.partnerName}}
          @partnerPhone={{@model.partnerPhone}}
          @partnerEmail={{@model.partnerEmail}}
        />
      {{/if}}
    {{/if}}
  </template>
}

export default ManageSubscriptionPage
